import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { i18n } from '../../translate/i18n'

type FilterOption = {
  label: string
  value: string | number
}

type ProductFilterProps = {
  isOpen: boolean
  activeFilters: FilterState
  setActiveFilters: React.Dispatch<React.SetStateAction<FilterState>>
  onClose: () => void
  onApplyFilters: (filters: FilterState) => void
  onClearFilters: () => void
  categories?: FilterOption[]
  groups?: FilterOption[]
  loading?: boolean
}

type FilterState = {
  categories: (string | number)[]
  status: string
  groups: (string | number)[]
}

const ProductFilter: React.FC<ProductFilterProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  activeFilters,
  setActiveFilters,
  categories = [],
  groups = [],
  loading = false
}) => {

  const statusOptions = [
    { label: 'Todos', value: '' },
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
   // { label: 'Em Estoque', value: 'in_stock' },
   // { label: 'Esgotado', value: 'out_of_stock' }
  ]

  const handleCategoryChange = (categoryId: string | number) => {
    setActiveFilters((prev: FilterState) => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter((id: string | number) => id !== categoryId)
        : [...prev.categories, categoryId]
    }))
  }

  const handleGroupChange = (groupId: string | number) => {
    setActiveFilters((prev: FilterState) => ({
      ...prev,
      groups: prev.groups.includes(groupId)
        ? prev.groups.filter((id: string | number) => id !== groupId)
        : [...prev.groups, groupId]
    }))
  }

  const handleStatusChange = (status: string) => {
    setActiveFilters((prev: FilterState) => ({
      ...prev,
      status
    }))
  }

  const handleApplyFilters = () => {
    onApplyFilters(activeFilters)
    onClose()
  }

  const handleClearFilters = () => {
    const clearedFilters = {
      categories: [],
      status: '',
      groups: []
    }
    setActiveFilters(clearedFilters)
    onClearFilters()
    onClose()
  }

  const hasActiveFilters =
    activeFilters.categories.length > 0 ||
    activeFilters.status !== '' ||
    activeFilters.groups.length > 0

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton className="bg-light-primary">
        <Modal.Title className="fw-bolder">
          <i className="fas fa-filter me-2 text-primary"></i>
          Filtros de Produtos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-6">
        <div className="row g-5">
          {/* Filtro de Categorias */}
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-bold text-dark">
                <i className="fas fa-tags me-2 text-info"></i>
                Categorias
              </label>
              <div className="card border border-light-info">
                <div className="card-body p-4" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <div key={category.value} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`category-${category.value}`}
                          checked={activeFilters.categories.includes(category.value)}
                          onChange={() => handleCategoryChange(category.value)}
                        />
                        <label
                          className="form-check-label fw-normal"
                          htmlFor={`category-${category.value}`}
                        >
                          {category.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Nenhuma categoria disponível
                    </div>
                  )}
                </div>
              </div>
              {activeFilters.categories.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    {activeFilters.categories.length} categoria(s) selecionada(s)
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Filtro de Grupos */}
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-bold text-dark">
                <i className="fas fa-layer-group me-2 text-warning"></i>
                Grupos
              </label>
              <div className="card border border-light-warning">
                <div className="card-body p-4" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {groups.length > 0 ? (
                    groups.map((group) => (
                      <div key={group.value} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`group-${group.value}`}
                          checked={activeFilters.groups.includes(group.value)}
                          onChange={() => handleGroupChange(group.value)}
                        />
                        <label
                          className="form-check-label fw-normal"
                          htmlFor={`group-${group.value}`}
                        >
                          {group.label}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      Nenhum grupo disponível
                    </div>
                  )}
                </div>
              </div>
              {activeFilters.groups.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    {activeFilters.groups.length} grupo(s) selecionado(s)
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Filtro de Status */}
          <div className="col-12">
            <div className="form-group">
              <label className="form-label fw-bold text-dark">
                <i className="fas fa-toggle-on me-2 text-success"></i>
                Status do Produto
              </label>
              <div className="card border border-light-success">
                <div className="card-body p-4">
                  <div className="row">
                    {statusOptions.map((option) => (
                      <div key={option.value} className="col-md-2 col-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="status"
                            id={`status-${option.value}`}
                            value={option.value}
                            checked={activeFilters.status === option.value}
                            onChange={() => handleStatusChange(option.value)}
                          />
                          <label
                            className="form-check-label fw-normal"
                            htmlFor={`status-${option.value}`}
                          >
                            {option.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo dos Filtros Ativos */}
          {hasActiveFilters && (
            <div className="col-12">
              <div className="alert alert-light-primary d-flex align-items-center p-4">
                <i className="fas fa-info-circle me-3 text-primary"></i>
                <div>
                  <strong>Filtros Ativos:</strong>
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {activeFilters.categories.length > 0 && (
                      <span className="badge badge-light-info">
                        {activeFilters.categories.length} Categoria(s)
                      </span>
                    )}
                    {activeFilters.groups.length > 0 && (
                      <span className="badge badge-light-warning">
                        {activeFilters.groups.length} Grupo(s)
                      </span>
                    )}
                    {activeFilters.status && (
                      <span className="badge badge-light-success">
                        Status: {statusOptions.find(s => s.value === activeFilters.status)?.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light p-4">
        <div className="d-flex justify-content-between w-100">
          <button
            type="button"
            className="btn btn-light-secondary fw-bold"
            onClick={onClose}
            disabled={loading}
          >
            <i className="fas fa-times me-2"></i>
            Cancelar
          </button>

          <div className="d-flex gap-3">
            <button
              type="button"
              className="btn btn-light-warning fw-bold"
              onClick={handleClearFilters}
              disabled={loading || !hasActiveFilters}
            >
              <i className="fas fa-eraser me-2"></i>
              Limpar Filtros
            </button>

            <button
              type="button"
              className="btn btn-primary fw-bold"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Aplicando...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Aplicar Filtros
                </>
              )}
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export { ProductFilter }